import { useState, useEffect, useContext, createContext } from 'react'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { ModalKey, ModalContext } from '../ModalContext'
import { addNewProgramTableOverride, removeProgramTableOverride, setWorkoutTemplateOverrideWeightRecommendationStorage } from 'redux/slices/athleteProfile'
import { resetActiveWorkoutPreviewState, setActiveWorkoutPreviewState } from 'redux/slices/activeWorkoutPreview'
import { GetActiveWorkout } from 'redux/slices/activeWorkout'
import { IsExerciseSetUnitSeconds } from 'functions/exerciseUtils'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import moment from 'moment'

import WorkoutTemplateModalRouteView from 'components/Modals/Components/WorkoutOverrideModal/WorkoutTemplateModalRouteView'
import CurrentExerciseInstructionsModalRouteView from 'components/Modals/Components/WorkoutOverrideModal/CurrentExerciseInstructionsModalRouteView'
import ExerciseListModalRouteView from 'components/Modals/Components/WorkoutOverrideModal/ExerciseListModalRouteView'
import ExerciseInstructionsModalRouteView from 'components/Modals/Components/WorkoutOverrideModal/ExerciseInstructionsModalRouteView'
import ExerciseCreateModalRouteView from 'components/Modals/Components/WorkoutOverrideModal/ExerciseCreateModalRouteView'

export const WorkoutOverrideModalContext = createContext()

export default function WorkoutOverrideModal() {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    
    const { modalData, ShowModal, HideModal } = useContext(ModalContext)
    const { exercises } = useGetExercises()

    const coachId = useSelector(state => state.user.id)
    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const groupName = useSelector(state => state.athleteProfile.groupName)
    const athleteId = useSelector(state => state.athleteProfile.userId)
    const athleteFirstName = useSelector(state => state.athleteProfile.firstName)
    const athleteLastName = useSelector(state => state.athleteProfile.lastName)
    const activeWorkout = useSelector((state) => state.activeWorkout)
    const cachedOverrides = useSelector((state) => state.athleteProfile.programTableOverrides);
    const workoutTemplateOverrideWeightRecommendationStorage = useSelector(state => state.athleteProfile.workoutTemplateOverrideWeightRecommendationStorage)
    const latestProgramSnapshot = useSelector(state => state.athleteProfile.latestProgramSnapshot)
    const programTableColumn = latestProgramSnapshot.programTable.find(week => week.weekOfTheYearKey === modalData.weekOfTheYearKey)

    const [modalRoute, setModalRoute] = useState('workout-template')
    const [currentExercise, setCurrentExercise] = useState(null)
    const [exerciseSwapIndices, setExerciseSwapIndices] = useState(null)
    const [swapExercise, setSwapExercise] = useState(null)

    const canApplyOneOffCoachOverrides = ff(flags.canApplyOneOffCoachOverrides, { profileType: profileType })

    const SearchForPhaseOverrideInCache = (overrideKey, phaseIndex) => {
        const overrideIndex = cachedOverrides.findIndex(o => 
            o.overrideKey === overrideKey 
            && (
                (profileType === 'group' && o.kinesisUserGroupId === parseInt(groupId)) ||
                (profileType === 'individual' && o.userId === parseInt(athleteId))
            ) 
            && o.target.weekOfTheYearKey === modalData.weekOfTheYearKey 
            && o.target.workoutTemplateKey === modalData.workoutTemplateKey 
            && o.target.phaseIndex === phaseIndex
        )

        if (overrideIndex === -1) {
            return null
        }

        return {
            index: overrideIndex,
            override: cachedOverrides[overrideIndex]
        }
    }

    const SearchForSlotOverrideInCache = (overrideKey, phaseIndex, slotIndex) => {
        const overrideIndex = cachedOverrides.findIndex(o => 
            o.overrideKey === overrideKey 
            && (
                (profileType === 'group' && o.kinesisUserGroupId === parseInt(groupId)) ||
                (profileType === 'individual' && o.userId === parseInt(athleteId))
            ) 
            && o.target.weekOfTheYearKey === modalData.weekOfTheYearKey 
            && o.target.workoutTemplateKey === modalData.workoutTemplateKey 
            && o.target.phaseIndex === phaseIndex
            && o.target.slotIndex === slotIndex
        )

        if (overrideIndex === -1) {
            return null
        }

        return {
            index: overrideIndex,
            override: cachedOverrides[overrideIndex]
        }
    }

    const SearchForSlotExerciseOverrideInCache = (overrideKey, phaseIndex, slotIndex, exerciseId) => {
        const overrideIndex = cachedOverrides.findIndex(o => 
            o.overrideKey === overrideKey 
            && (
                (profileType === 'group' && o.kinesisUserGroupId === parseInt(groupId)) ||
                (profileType === 'individual' && o.userId === parseInt(athleteId))
            )  
            && o.target.weekOfTheYearKey === modalData.weekOfTheYearKey 
            && o.target.workoutTemplateKey === modalData.workoutTemplateKey 
            && o.target.phaseIndex === phaseIndex
            && o.target.slotIndex === slotIndex
            && o.target.exerciseId === exerciseId
        )

        if (overrideIndex === -1) {
            return null
        }

        return {
            index: overrideIndex,
            override: cachedOverrides[overrideIndex]
        }
    }

    const GetWorkoutTemplateDisplayData = (overrideKey, phaseIndex, slotIndex = 0, exerciseId = 0) => {

        switch (overrideKey) {

            case 'one-off-sets-override':
                
                const setsOverride = SearchForPhaseOverrideInCache(overrideKey, phaseIndex)
                
                if (setsOverride !== null) {
                    return {
                        overrideApplied: true,
                        value: setsOverride.override.target.sets
                    }
                }

                return {
                    overrideApplied: false,
                    value: programTableColumn.volumeData.setsAndRepsData[modalData.workoutTemplateKey][phaseIndex].sets
                }

            case 'one-off-exercise-swap':

                var databaseExercise = null

                const exerciseOverride = SearchForSlotOverrideInCache(overrideKey, phaseIndex, slotIndex)
                
                if (exerciseOverride !== null) {
                    databaseExercise = exercises.find(e => e.id === exerciseOverride.override.target.exerciseId) || null

                    return {
                        overrideApplied: true,
                        value: databaseExercise
                    }
                }

                const originalExerciseId = programTableColumn.exerciseData[modalData.workoutTemplateKey][phaseIndex].slots[slotIndex].id

                databaseExercise = exercises.find(e => e.id === originalExerciseId) || null

                return {
                    overrideApplied: false,
                    value: databaseExercise
                }

            case 'one-off-reps-override':

                // Grab exercise to calculate volume data from. We need this is overriding between setUnits

                var currentExercise = null

                const tempExerciseOverride = SearchForSlotOverrideInCache('one-off-exercise-swap', phaseIndex, slotIndex)
                const originalExercise = programTableColumn.exerciseData[modalData.workoutTemplateKey][phaseIndex].slots[slotIndex]

                if (tempExerciseOverride !== null) {
                    const tempOverrideExerciseId = tempExerciseOverride.override.target.exerciseId
                    const tempOverrideDatabaseExercise = exercises.find(e => e.id === tempOverrideExerciseId) || null
                    currentExercise = tempOverrideDatabaseExercise
                } else {
                    currentExercise = originalExercise
                }

                // Check for set units override

                const setUnitsOverride = SearchForSlotOverrideInCache(overrideKey, phaseIndex, slotIndex)

                if (setUnitsOverride !== null) {
                    return {
                        overrideApplied: true,
                        value: setUnitsOverride.override.target.reps
                    }
                }

                const slotConfig = latestProgramSnapshot.slotExerciseProgressionConfig[modalData.workoutTemplateKey].phases[phaseIndex].slots[slotIndex]
                const phaseVolumeData = programTableColumn.volumeData.setsAndRepsData[modalData.workoutTemplateKey][phaseIndex]
                
                // Get exercise lateral. Default to bilateral if not found.

                var originalExerciseLateral = currentExercise.lateral

                if (
                    originalExerciseLateral !== 'unilateral' &&
                    originalExerciseLateral !== 'bilateral'
                ) {
                    var tempDatabaseExercise = exercises.find(e => e.id === currentExercise.id) || null
                    originalExerciseLateral = tempDatabaseExercise.lateral
                }

                if (
                    originalExerciseLateral !== 'unilateral' &&
                    originalExerciseLateral !== 'bilateral'
                ) {
                    console.error('Could not find exercise lateral for exercise:', currentExercise.id)
                    originalExerciseLateral = 'bilateral'
                }

                // TODO: Change this
                // Hard coding seconds exercises since getting database exercise for every cell is inefficent
                // We should be caching setUnit

                if (IsExerciseSetUnitSeconds(currentExercise.id) === true) {
                    
                    if (
                        currentExercise.id === 386 // Front Side Flutter
                        || currentExercise.id === 387 // Back Side Flutter
                    ) {
                        return {
                            overrideApplied: false,
                            value: 30
                        }
                    }

                    return {
                        overrideApplied: false,
                        value: 10
                    }
                }

                // TODO: Change this
                // Displaying plyometric volume override in program table
                // We should really be caching category. This method works for now but is inefficent and could miss cells (exercise override for example)

                if (slotConfig?.progressionFilters?.category && slotConfig.progressionFilters.category.includes('plyometric')) {
                    
                    const originalDatabaseExercise = exercises.find(e => e.id === currentExercise.id) || null

                    // console.log(originalDatabaseExercise)

                    if (
                        (originalDatabaseExercise.category === 'plyometric-bilateral' || originalDatabaseExercise.category === 'plyometric-unilateral') &&
                        // Check that the coach has not overridden the reps for this exercise
                        (phaseVolumeData.reps[slotIndex]?.overrideApplied === undefined || phaseVolumeData.reps[slotIndex]?.overrideApplied === false)
                    ) {

                        const plyometricOverrideData = programTableColumn.volumeData.plyometricSetsAndRepsOverrideData

                        // console.log(plyometricOverrideData)

                        return {
                            overrideApplied: false,
                            value: plyometricOverrideData[originalExerciseLateral]
                        }
                    }

                }

                // Slot specific reps data

                if (originalExerciseLateral === 'unilateral') {
                    return {
                        overrideApplied: false,
                        value: phaseVolumeData.reps[slotIndex].unilateral
                    }
                }
        
                if (originalExerciseLateral === 'bilateral') {
                    return {
                        overrideApplied: false,
                        value: phaseVolumeData.reps[slotIndex].bilateral
                    }
                }
        
                return 0

            case 'one-off-weight-recommendation-override': 

                const cachedOverrideDetails = SearchForSlotExerciseOverrideInCache(overrideKey, phaseIndex, slotIndex, exerciseId)

                if (cachedOverrideDetails !== null) {
                    return {
                        overrideApplied: true,
                        value: cachedOverrideDetails.override.target.weightRecommendation
                    }
                }

                const originalWeightRecommendation = workoutTemplateOverrideWeightRecommendationStorage[phaseIndex][slotIndex]

                return {
                    overrideApplied: false,
                    value: originalWeightRecommendation
                }
        
            default: 
                console.error('Invalid override key:', overrideKey)
                return null
            
        }

    }

    const HandleOverrideChange = (overrideKey, phaseIndex, slotIndex, value) => {

        switch (overrideKey) {

            case 'one-off-sets-override':

                if (value === 0) {
                    return
                }
                    
                const setsOverride = SearchForPhaseOverrideInCache(overrideKey, phaseIndex)
        
                if (setsOverride !== null) {
                    dispatch(removeProgramTableOverride(setsOverride.index))
                }

                const originalSets = programTableColumn.volumeData.setsAndRepsData[modalData.workoutTemplateKey][phaseIndex].sets
        
                if (value !== originalSets) {
                    const newOverride = {
                        overrideKey: overrideKey,
                        kinesisUserGroupId: groupId,
                        userId: athleteId,
                        source: {
                            coachId: coachId
                        },
                        target: {
                            weekOfTheYearKey: modalData.weekOfTheYearKey,
                            workoutTemplateKey: modalData.workoutTemplateKey,
                            phaseIndex: parseInt(phaseIndex),
                            sets: parseInt(value)
                        },
                        createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                        archived: 0
                    }
        
                    dispatch(addNewProgramTableOverride(newOverride))
                }

                break

            case 'one-off-exercise-swap':

                // Reset weight recommendation overrides for this slot. Use the slot search function since we want to clear it for any exercise

                const originalExerciseWeightRecommendationOverride = SearchForSlotOverrideInCache('one-off-weight-recommendation-override', phaseIndex, slotIndex)

                if (originalExerciseWeightRecommendationOverride !== null) {
                    dispatch(removeProgramTableOverride(originalExerciseWeightRecommendationOverride.index))
                }

                const cachedExerciseOverrideDetails = SearchForSlotOverrideInCache(overrideKey, phaseIndex, slotIndex)
        
                if (cachedExerciseOverrideDetails !== null) {
                    dispatch(removeProgramTableOverride(cachedExerciseOverrideDetails.index))
                }

                const originalExerciseId = programTableColumn.exerciseData[modalData.workoutTemplateKey][phaseIndex].slots[slotIndex].id

                if (value.id !== originalExerciseId) {
                    const newOverride = {
                        overrideKey: overrideKey,
                        kinesisUserGroupId: groupId,
                        userId: athleteId,
                        source: {
                            coachId: coachId
                        },
                        target: {
                            weekOfTheYearKey: modalData.weekOfTheYearKey,
                            workoutTemplateKey: modalData.workoutTemplateKey,
                            phaseIndex: parseInt(phaseIndex),
                            slotIndex: parseInt(slotIndex),
                            exerciseId: value.id,
                            exerciseName: value.name,
                            lateral: value.lateral,
                            progressionLevel: value.progressionLevel,
                        },
                        createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                        archived: 0
                    }
        
                    dispatch(addNewProgramTableOverride(newOverride))

                    setCurrentExercise(null)
                    setExerciseSwapIndices(null)
                    setSwapExercise(null)
                }

                break

            case 'one-off-reps-override':

                if (value === 0){
                    return
                }

                const cachedRepsOverrideDetails = SearchForSlotOverrideInCache(overrideKey, phaseIndex, slotIndex)
        
                if (cachedRepsOverrideDetails !== null) {
                    dispatch(removeProgramTableOverride(cachedRepsOverrideDetails.index))
                }

                var originalRepsValue = 0
                const phaseVolumeData = programTableColumn.volumeData.setsAndRepsData[modalData.workoutTemplateKey][phaseIndex]
                const originalExerciseLateral = programTableColumn.exerciseData[modalData.workoutTemplateKey][phaseIndex].slots[slotIndex].lateral

                if (originalExerciseLateral === 'unilateral') {
                    originalRepsValue = phaseVolumeData.reps[slotIndex].unilateral
                }

                if (originalExerciseLateral === 'bilateral') {
                    originalRepsValue = phaseVolumeData.reps[slotIndex].bilateral
                }
        
                if (value !== originalRepsValue) {
                    const newOverride = {
                        overrideKey: overrideKey,
                        kinesisUserGroupId: groupId,
                        userId: athleteId,
                        source: {
                            coachId: coachId
                        },
                        target: {
                            weekOfTheYearKey: modalData.weekOfTheYearKey,
                            workoutTemplateKey: modalData.workoutTemplateKey,
                            phaseIndex: parseInt(phaseIndex),
                            slotIndex: parseInt(slotIndex),
                            reps: parseInt(value)
                        },
                        createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                        archived: 0
                    }
        
                    dispatch(addNewProgramTableOverride(newOverride))
                }

                break

            case 'one-off-weight-recommendation-override':

                if (value === 0){
                    return
                }

                const cachedWeightOverrideDetails = SearchForSlotOverrideInCache(overrideKey, phaseIndex, slotIndex)
        
                if (cachedWeightOverrideDetails !== null) {
                    dispatch(removeProgramTableOverride(cachedWeightOverrideDetails.index))
                }

                const originalWeightRecommendation = workoutTemplateOverrideWeightRecommendationStorage[phaseIndex][slotIndex]

                if (value.weightRecommendation !== originalWeightRecommendation) {
                    const newOverride = {
                        overrideKey: overrideKey,
                        kinesisUserGroupId: groupId,
                        userId: athleteId,
                        source: {
                            coachId: coachId
                        },
                        target: {
                            weekOfTheYearKey: modalData.weekOfTheYearKey,
                            workoutTemplateKey: modalData.workoutTemplateKey,
                            phaseIndex: parseInt(phaseIndex),
                            slotIndex: parseInt(slotIndex),
                            exerciseId: parseInt(value.exerciseId),
                            weightRecommendation: parseInt(value.weightRecommendation)
                        },
                        createdTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                        archived: 0
                    }
        
                    dispatch(addNewProgramTableOverride(newOverride))
                }

                break

            default:
                console.error('Invalid override key:', overrideKey)
                break
            
        }

    }

    const StartWorkout = () => {

        // Convert profile type to payload profile type. Basically just convert group-individual to group

        var payloadProfileType = null

        if (profileType === 'group' || profileType === 'group-individual') {
            payloadProfileType = 'group'
        }

        if (profileType === 'individual') {
            payloadProfileType = 'individual'
        }

        if (payloadProfileType === null) {
            console.error('Invalid profile type:', profileType)
            return
        }

        // Check for already active group workout

        if ((payloadProfileType === 'group') && GetActiveWorkout(activeWorkout, payloadProfileType, groupId, athleteId) !== null) {
            const modalData = {
                key: ModalKey.Text,
                fullscreen: false,
                titleText: 'Cannot Start Another Workout',
                bodyText: [
                    'A workout for this group is already active.',
                    'Please complete your current workout before starting a new one for this group.',
                    'Contact support@kinesisintegrated.com for further assistance.' 
                ],
                buttonText: 'OK',
                buttonAction: () => HideModal()
            }
    
            ShowModal(modalData)

            return;
        }

        // CHeck for already active individual workout

        if (payloadProfileType === 'individual' && GetActiveWorkout(activeWorkout, payloadProfileType, groupId, athleteId) !== null) {
            const modalData = {
                key: ModalKey.Text,
                fullscreen: false,
                titleText: 'Cannot Start Another Workout',
                bodyText: [
                    'A workout for this athlete is already active.',
                    'Please complete your current workout before starting a new one for this athlete.',
                    'Contact support@kinesisintegrated.com for further assistance.' 
                ],
                buttonText: 'OK',
                buttonAction: () => HideModal()
            }
    
            ShowModal(modalData)

            return;
        }

        // Send data to active workout preview

        dispatch(resetActiveWorkoutPreviewState())

        const payload = {
            profileType: payloadProfileType,
            groupId: groupId,
            groupName: groupName,
            athleteId: athleteId,
            athleteFirstName: athleteFirstName,
            athleteLastName: athleteLastName,
            weekOfTheYearKey: modalData.weekOfTheYearKey,
            workoutTemplateKey: modalData.workoutTemplateKey,
            taperData: programTableColumn.volumeData.taperData,
            programPhaseKey: programTableColumn.volumeData.programPhaseKey,
            programPhaseWeekIndex: programTableColumn.volumeData.programPhaseWeekIndex
        }

        // console.log(payload)

        dispatch(setActiveWorkoutPreviewState(payload))

        navigate('/active/workout/preview');

        HideModal()
    }

    // Workout Override Modal Context Data

    const workoutOverrideModalContextData = {
        // Data
        exercises,
        programTableColumn,
        allowEditing: (canApplyOneOffCoachOverrides && (modalData.workoutTemplateKey === 'strength-session-a' || modalData.workoutTemplateKey === 'strength-session-b')),
        // Hooks
        setModalRoute,
        currentExercise,
        setCurrentExercise,
        exerciseSwapIndices,
        setExerciseSwapIndices,
        swapExercise,
        setSwapExercise,
        // Functions
        GetWorkoutTemplateDisplayData,
        HandleOverrideChange,
        StartWorkout
    }

    useEffect(() => {

        var tempWeightRecommendationStorage = {}

        programTableColumn.exerciseData[modalData.workoutTemplateKey].map((phase, phaseIndex) => {

            tempWeightRecommendationStorage[phaseIndex] = {}

            phase.slots.map((_, slotIndex) => {

                tempWeightRecommendationStorage[phaseIndex][slotIndex] = null

            })
        })

        dispatch(setWorkoutTemplateOverrideWeightRecommendationStorage(tempWeightRecommendationStorage))

    }, [])

    if (exercises.length === 0 || workoutTemplateOverrideWeightRecommendationStorage === null){ 
        return (
            <div className='flex flex-col h-[75vh] overflow-y-auto'>
                <div className='space-y-4 p-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </div>
        )
    }

    return (
        <WorkoutOverrideModalContext.Provider value={workoutOverrideModalContextData}>
            {/* <div className='grid grid-cols-3'>
                <p className='text-xxs'>currentExercise: {currentExercise?.id}</p>
                <p className='text-xxs'>exerciseSwapIndices: {exerciseSwapIndices?.phaseIndex} {exerciseSwapIndices?.slotIndex}</p>
                <p className='text-xxs'>swapExercise: {swapExercise?.id}</p>
            </div> */}

            {modalRoute === 'workout-template' && <WorkoutTemplateModalRouteView />}

            {modalRoute === 'current-exercise-instructions' && <CurrentExerciseInstructionsModalRouteView />}

            {modalRoute === 'exercise-list' && <ExerciseListModalRouteView />}

            {modalRoute === 'exercise-instructions' && <ExerciseInstructionsModalRouteView />}

            {modalRoute === 'exercise-create' && <ExerciseCreateModalRouteView />}

        </WorkoutOverrideModalContext.Provider>
    )
}